import { Tab, Tabs, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DriversIcon } from '../../../../assets/svg/Plan/MenuBar/DriversIcon';
import { EditIcon } from '../../../../assets/svg/Plan/MenuBar/EditIcon';
import { OverviewIcon } from '../../../../assets/svg/Plan/MenuBar/OverviewIcon';
import { StopsIcon } from '../../../../assets/svg/Plan/MenuBar/StopsIcon';
import * as routing from '../../../../constants/Routing';
import { PlanFlat } from '../../../../domain/plan/PlanFlat';
import { Project } from '../../../../domain/project/Project';
import { intl } from '../../../../helpers/IntGlobalProvider';
import eventsPanel from '../../../../services/events';

interface StyledTabsProps {
  children?: React.ReactNode;
  value: string;
  onChange: (event: React.SyntheticEvent, newValue: string) => void;
}

const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs {...props} TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }} />
))({
  width: `100%`,
  height: `100%`,
  '& .MuiTabs-indicator': {
    display: `flex`,
    justifyContent: `center`,
    backgroundColor: `transparent`,
  },
  '& .MuiTabs-indicatorSpan': {
    width: `100%`,
    backgroundColor: RoutalPalette.primary40,
  },
  '& .MuiTabs-flexContainer': {
    height: `100%`,
  },
  '& button': {
    minHeight: `54px`,
  },
  borderRight: `1px solid ${RoutalPalette.grey5}`,
});

interface StyledTabProps {
  label?: string;
  icon: React.ReactElement;
  value: string;
}

const StyledTab = (props: StyledTabProps) => {
  const [isHover, setHover] = useState<boolean>(false);
  const isMediumDesktop = useMediaQuery(`(max-width: 1440px)`);
  const { label, icon, ...other } = props;
  const newIcon = React.cloneElement(icon, { isHover });
  return (
    <StyledTabContent
      {...other}
      icon={newIcon}
      {...(!isMediumDesktop ? { label } : {})}
      // @ts-ignore
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    />
  );
};

const StyledTabContent = styled((props: StyledTabProps) => <Tab disableRipple {...props} />)(({ theme }) => ({
  display: `flex`,
  flex: 1,
  minWidth: `24px`,
  textTransform: `none`,
  fontWeight: 400,
  fontSize: theme.typography.pxToRem(14),
  color: RoutalPalette.neutral60,
  '&.Mui-selected': {
    color: RoutalPalette.primary40,
    '&. MuiSvgIcon-root': {
      color: RoutalPalette.primary40,
    },
  },

  // '@media (max-width: 768px)': {
  //   backgroundColor: `red`,
  // },

  // TODO: BREAKPOINTS 1

  '&:hover': {
    color: RoutalPalette.primary20,
  },
  '&.Mui-focusVisible': {
    backgroundColor: `rgba(100, 95, 228, 0.32)`,
  },
}));

interface PlanTabsProps {
  activePlan?: PlanFlat;
  activeProject?: Project;
}

export const PlanTabs = ({ activePlan, activeProject }: PlanTabsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = React.useState<string>(`overview`);

  useEffect(() => {
    if (location.pathname) {
      if (location.pathname.includes(`overview`)) {
        setValue(`overview`);
      }
      if (location.pathname.includes(`drivers`)) {
        setValue(`drivers`);
      }
      if (location.pathname.includes(`stops`)) {
        setValue(`stops`);
      }
      if (location.pathname.includes(`edit`)) {
        setValue(`edit`);
      }
    }
  }, [location.pathname]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    if (activeProject && activePlan) {
      switch (newValue) {
        case `overview`:
          navigate(
            routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(`:projectId`, activeProject.id).replace(
              `:planId`,
              activePlan.id
            )
          );
          eventsPanel.planMenu(`overview`);
          break;
        case `drivers`:
          navigate(
            routing.PLANNER.PLAN_DETAILS.DRIVERS.replace(`:projectId`, activeProject.id).replace(
              `:planId`,
              activePlan.id
            )
          );
          eventsPanel.planMenu(`drivers`);
          break;
        case `stops`:
          navigate(
            routing.PLANNER.PLAN_DETAILS.STOPS.replace(`:projectId`, activeProject.id).replace(`:planId`, activePlan.id)
          );
          eventsPanel.planMenu(`stops`);
          break;
        case `edit`:
          navigate(
            routing.PLANNER.PLAN_DETAILS.EDIT.replace(`:projectId`, activeProject.id).replace(`:planId`, activePlan.id)
          );
          eventsPanel.planMenu(`edit`);
          break;
      }
    }
  };

  return (
    <StyledTabs value={value} onChange={handleChange} aria-label="styled tabs">
      <StyledTab
        icon={
          <OverviewIcon
            filled={value === `overview`}
            color={value === `overview` ? RoutalPalette.primary40 : RoutalPalette.neutral60}
            hoverColor={RoutalPalette.primary20}
            style={{ height: `20px`, width: `20px` }}
          />
        }
        label={intl.formatMessage({ id: `planner.plan_details.header.menu.overview` })}
        value="overview"
      />
      <StyledTab
        icon={
          <DriversIcon
            filled={value === `drivers`}
            color={value === `drivers` ? RoutalPalette.primary40 : RoutalPalette.neutral60}
            hoverColor={RoutalPalette.primary20}
            style={{ height: `21px`, width: `21px` }}
          />
        }
        label={intl.formatMessage({ id: `planner.plan_details.header.menu.drivers` })}
        value="drivers"
      />
      <StyledTab
        icon={
          <StopsIcon
            filled={value === `stops`}
            color={value === `stops` ? RoutalPalette.primary40 : RoutalPalette.neutral60}
            hoverColor={RoutalPalette.primary20}
            style={{ height: `21px`, width: `21px` }}
          />
        }
        label={intl.formatMessage({ id: `planner.plan_details.header.menu.stops` })}
        value="stops"
      />
      <StyledTab
        icon={
          <EditIcon
            filled={value === `edit`}
            color={value === `edit` ? RoutalPalette.primary40 : RoutalPalette.neutral60}
            hoverColor={RoutalPalette.primary20}
            style={{ height: `19px`, width: `19px` }}
          />
        }
        label={intl.formatMessage({ id: `planner.plan_details.header.menu.edit` })}
        value="edit"
      />
    </StyledTabs>
  );
};
